import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { DEACTIVATE_JOB_OFFER, DEACTIVATE_JOB_OFFER_PRO } from '@gql/jobs';
import JobOfferDeactivationDialog from './JobOfferDeactivationDialog';
import { AssignJobOfferDialog } from './AssignJobOfferDialog';

interface JobOfferRowActionsProps {
    row: {
        _id: string;
        jobNumber?: string;
        status?: string;
        publicLink?: string;
        title?: string;
        assignees?: any[];
        pro?: {
            _id: string;
        };
    };
    onSuccess?: () => void;
}

const JobOfferRowActions: React.FC<JobOfferRowActionsProps> = ({ row, onSuccess }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { isUpercut } = useAuth();
    const { isProIdRequired } = useProId();
    const { showSnackbar } = useSnackbar();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const [deactivationDialogOpen, setDeactivationDialogOpen] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const open = Boolean(anchorEl);

    const [deactivateUpercut] = useMutation(DEACTIVATE_JOB_OFFER);
    const [deactivatePro] = useMutation(DEACTIVATE_JOB_OFFER_PRO);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleClose();
        navigate(`/job-offers/edit/${row.jobNumber}`);
    };

    const handleDuplicate = () => {
        handleClose();
        navigate(`/job-offers/add?fromJobOffer=${row.jobNumber}`);
    };

    const handleDeactivate = () => {
        handleClose();
        setDeactivationDialogOpen(true);
    };

    const handleAssign = () => {
        handleClose();
        setAssignDialogOpen(true);
    };

    const handleConfirmDeactivate = async () => {
        setIsLoadingSubmit(true);
        try {
            const mutate = isUpercut && !isProIdRequired ? deactivateUpercut : deactivatePro;
            await mutate({ variables: { id: row._id } });
            showSnackbar('Offre désactivée avec succès', 'success');
            onSuccess?.();
        } catch (error) {
            showSnackbar(error.message, 'error');
        } finally {
            setIsLoadingSubmit(false);
            setDeactivationDialogOpen(false);
        }
    };

    const handleViewPublicPage = () => {
        handleClose();
        if (row.publicLink) {
            window.open(row.publicLink, '_blank', 'noopener,noreferrer');
        }
    };

    const isDraft = row.status === 'DRAFT';
    const isActive = row.status === 'ACTIVE';

    return (
        <>
            <IconButton size="small" onClick={handleOpen} sx={{ color: theme.palette.text.secondary }}>
                <i className="fi fi-br-menu-dots" style={{ fontSize: 14 }} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '12px',
                            minWidth: 180,
                            background: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.border?.main ?? theme.palette.divider}`,
                        },
                    },
                }}
            >
                <MenuItem dense component={Link} to={`/job-offers/${row.jobNumber}/detail`}>
                    <i className="fi fi-rr-edit" style={{ marginRight: 10, fontSize: 14 }} />
                    <Typography variant="body2">Modifier</Typography>
                </MenuItem>

                <MenuItem dense onClick={handleDuplicate}>
                    <i className="fi fi-rr-copy" style={{ marginRight: 10, fontSize: 14 }} />
                    <Typography variant="body2">Dupliquer</Typography>
                </MenuItem>

                <MenuItem dense onClick={handleAssign}>
                    <i className="fi fi-rr-user-add" style={{ marginRight: 10, fontSize: 14 }} />
                    <Typography variant="body2">Attribuer</Typography>
                </MenuItem>

                {!isDraft && (
                    <MenuItem dense component={Link} to={row.publicLink ?? '#'} target="_blank" rel="noopener noreferrer">
                        <i className="fi fi-rr-eye" style={{ marginRight: 10, fontSize: 14 }} />
                        <Typography variant="body2">Voir la page publique</Typography>
                    </MenuItem>
                )}

                {isActive && (
                    <MenuItem onClick={handleDeactivate} dense>
                        <i
                            className="fi fi-rr-ban"
                            style={{ marginRight: 10, fontSize: 14, color: theme.palette.errorRed?.main ?? theme.palette.error.main }}
                        />
                        <Typography variant="body2" sx={{ color: theme.palette.errorRed?.main ?? theme.palette.error.main }}>
                            Désactiver
                        </Typography>
                    </MenuItem>
                )}
            </Menu>
            <JobOfferDeactivationDialog
                open={deactivationDialogOpen}
                jobOfferId={row._id}
                proId={row.pro?._id}
                onClose={() => setDeactivationDialogOpen(false)}
                onConfirm={handleConfirmDeactivate}
                isLoadingSubmit={isLoadingSubmit}
            />
            <AssignJobOfferDialog
                open={assignDialogOpen}
                onClose={() => setAssignDialogOpen(false)}
                job={{
                    _id: row._id,
                    title: row.title,
                    proId: row.pro?._id,
                    assignees: row.assignees,
                }}
                onSelected={() => {}}
            />
        </>
    );
};

export default JobOfferRowActions;
